import { useEffect, useMemo, useState } from 'react';

import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import { assignLocalizedPublicPath } from '@/utils/localePublicUrl';

import './styles/LimitedEditionOwnersPopup.css';

const SIZE_ORDER = {
  small: 0,
  medium: 1,
  large: 2,
};
const SIZE_KEYS = ['small', 'medium', 'large'];
const FALLBACK_SIZE_KEY = 'edition';
const INITIAL_PAGE_SIZE = 20;
const PAGE_SIZE_STEP = 20;

function normalizeSizeKey(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.toLowerCase().replace(/\s+/g, '_');
}

function formatSizeLabelFromKey(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const cleaned = value.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) {
    return null;
  }
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeEditionSize(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function formatSoldCopy(count, editionSize) {
  if (!Number.isFinite(count)) {
    return '';
  }
  const editionTotal = normalizeEditionSize(editionSize);
  if (editionTotal) {
    return `${count} / ${editionTotal}`;
  }
  return count === 1 ? '1 sold' : `${count} sold`;
}

function resolveDisplayedSoldCount(group) {
  if (!group) {
    return null;
  }
  const ownerCount = Array.isArray(group.owners) ? group.owners.length : 0;
  if (Number.isFinite(group.soldCount)) {
    return Math.max(group.soldCount, ownerCount);
  }
  return ownerCount > 0 ? ownerCount : null;
}

export default function LimitedEditionOwnersPopup({
  open = false,
  onClose,
  owners = [],
  editionSizes = {},
  editionSoldCounts = {},
  forceSequentialEditionNumbers = false,
  forceDiscontinuedCounts = false,
  discontinuedTotal = null,
  onNavigateProfile = null,
}) {
  const editionSizeMap = useMemo(() => {
    if (!editionSizes || typeof editionSizes !== 'object') {
      return {};
    }
    return Object.keys(editionSizes).reduce((accumulator, key) => {
      const normalizedKey = normalizeSizeKey(key) ?? key;
      const value = normalizeEditionSize(editionSizes[key]);
      if (normalizedKey && value) {
        accumulator[normalizedKey] = value;
      }
      return accumulator;
    }, {});
  }, [editionSizes]);

  const editionSoldMap = useMemo(() => {
    if (!editionSoldCounts || typeof editionSoldCounts !== 'object') {
      return {};
    }
    return Object.keys(editionSoldCounts).reduce((accumulator, key) => {
      const normalizedKey = normalizeSizeKey(key) ?? key;
      const value = Number(editionSoldCounts[key]);
      if (normalizedKey && Number.isFinite(value)) {
        accumulator[normalizedKey] = value;
      }
      return accumulator;
    }, {});
  }, [editionSoldCounts]);

  const sizeGroups = useMemo(() => {
    if (!Array.isArray(owners) || !owners.length) {
      return [];
    }

    const grouped = new Map();
    owners.forEach((owner, index) => {
      if (!owner) {
        return;
      }
      const normalizedKey = normalizeSizeKey(owner.sizeKey ?? owner.sizeLabel);
      const bucketKey = SIZE_KEYS.includes(normalizedKey) ? normalizedKey : FALLBACK_SIZE_KEY;
      const label = owner.sizeLabel
        ?? formatSizeLabelFromKey(owner.sizeKey)
        ?? (normalizedKey ? formatSizeLabelFromKey(normalizedKey) : 'Edition');
      const editionSize = normalizeEditionSize(owner.editionSize)
        ?? editionSizeMap[normalizedKey ?? bucketKey]
        ?? editionSizeMap[bucketKey]
        ?? null;
      const soldCountRaw =
        (Number.isFinite(owner.sold) ? Number(owner.sold) : null)
        ?? editionSoldMap[normalizedKey ?? bucketKey]
        ?? editionSoldMap[bucketKey]
        ?? null;
      const soldCount = Number.isFinite(soldCountRaw) ? soldCountRaw : null;

      if (!grouped.has(bucketKey)) {
        grouped.set(bucketKey, {
          key: bucketKey,
          rawKey: normalizedKey,
          label,
          editionSize,
          soldCount,
          owners: [],
          order: index,
        });
      }

      const bucket = grouped.get(bucketKey);
      bucket.owners.push(owner);
      if (!bucket.label && label) {
        bucket.label = label;
      }
      if (!bucket.editionSize && editionSize) {
        bucket.editionSize = editionSize;
      }
      if (!Number.isFinite(bucket.soldCount) && Number.isFinite(soldCount)) {
        bucket.soldCount = soldCount;
      }
    });

    Object.entries(editionSoldMap).forEach(([sizeKey, soldCount], index) => {
      if (grouped.has(sizeKey)) {
        const group = grouped.get(sizeKey);
        if (!Number.isFinite(group.soldCount)) {
          group.soldCount = soldCount;
        }
        return;
      }

      grouped.set(sizeKey, {
        key: sizeKey,
        rawKey: sizeKey,
        label: formatSizeLabelFromKey(sizeKey) ?? 'Edition',
        editionSize: editionSizeMap[sizeKey] ?? null,
        soldCount,
        owners: [],
        order: owners.length + index,
      });
    });

    const prioritized = SIZE_KEYS
      .map((key) => grouped.get(key))
      .filter(Boolean);

    if (prioritized.length > 0) {
      return prioritized;
    }

    return Array.from(grouped.values()).sort((a, b) => {
      const orderDiff = (SIZE_ORDER[a.rawKey ?? ''] ?? 99) - (SIZE_ORDER[b.rawKey ?? ''] ?? 99);
      if (orderDiff !== 0) {
        return orderDiff;
      }
      if (a.label && b.label && a.label !== b.label) {
        return a.label.localeCompare(b.label);
      }
      return a.order - b.order;
    });
  }, [editionSizeMap, editionSoldMap, owners]);

  const [activeSizeKey, setActiveSizeKey] = useState(() => sizeGroups[0]?.key ?? null);
  const [visibleCounts, setVisibleCounts] = useState({});

  const activeGroup = sizeGroups.find((group) => group.key === activeSizeKey) ?? sizeGroups[0] ?? null;
  const activeOwners = activeGroup?.owners ?? [];
  const activeGroupEditionSize = forceDiscontinuedCounts
    ? activeOwners.length
    : activeGroup?.editionSize;

  useEffect(() => {
    if (!sizeGroups.length) {
      setActiveSizeKey(null);
      return;
    }
    if (!sizeGroups.some((group) => group.key === activeSizeKey)) {
      setActiveSizeKey(sizeGroups[0].key);
    }
  }, [sizeGroups, activeSizeKey]);

  useEffect(() => {
    if (!activeGroup) {
      return;
    }
    setVisibleCounts((prev) => {
      const current = prev[activeGroup.key];
      const desired = current && current > INITIAL_PAGE_SIZE ? current : INITIAL_PAGE_SIZE;
      if (current === desired) {
        return prev;
      }
      return {
        ...prev,
        [activeGroup.key]: desired,
      };
    });
  }, [activeGroup]);

  if (!open) {
    return null;
  }

  const handleProfileNavigate = (username) => {
    if (!username) {
      return;
    }
    if (typeof onNavigateProfile === 'function') {
      onNavigateProfile(username);
    } else {
      assignLocalizedPublicPath(`/user/${username}`);
    }
  };

  const ownersLimit = Math.min(
    activeOwners.length,
    visibleCounts[activeGroup?.key] ?? INITIAL_PAGE_SIZE,
  );
  const ownersToDisplay = activeOwners.slice(0, ownersLimit);
  const hasOwners = ownersToDisplay.length > 0;

  const handleListScroll = (event) => {
    if (!activeGroup) {
      return;
    }
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 8) {
      setVisibleCounts((prev) => {
        const current = prev[activeGroup.key] ?? INITIAL_PAGE_SIZE;
        if (current >= activeOwners.length) {
          return prev;
        }
        const next = Math.min(activeOwners.length, current + PAGE_SIZE_STEP);
        if (next === current) {
          return prev;
        }
        return {
          ...prev,
          [activeGroup.key]: next,
        };
      });
    }
  };

  return (
    <GenericPopup
      open={open}
      onBackgroundClick={onClose}
      className="limited-edition-owners-popup limited-edition-owners-popup__card remove-scrollbar"
      backgroundClassName="limited-edition-owners-popup__overlay"
      opacity
    >
      <div className="generic-popup__header">
        <h3 className="generic-popup__header-text">LEs Collectors</h3>
        <XIcon className="generic-popup__close" onClick={onClose} />
      </div>
      <div className="limited-edition-owners-popup__body">
        {!sizeGroups.length ? (
          <p className="limited-edition-owners-popup__empty">
            No limited edition owners yet.
          </p>
        ) : (
          <>
            {Number.isFinite(discontinuedTotal) && discontinuedTotal > 0 ? (
              <p className="limited-edition-owners-popup__discontinued-note">
                This edition is closed due to removal of the original artwork.
                Only {discontinuedTotal} {discontinuedTotal === 1 ? 'edition has' : 'editions have'} been produced, and no further editions can be sold on Suarte or elsewhere.
              </p>
            ) : null}
            <div className="limited-edition-owners-popup__tabs" role="tablist">
              {sizeGroups.map((group) => {
                const isActive = activeGroup?.key === group.key;
                const groupSoldCountRaw = resolveDisplayedSoldCount(group);
                const groupSoldCount = forceDiscontinuedCounts
                  ? group.owners.length
                  : groupSoldCountRaw;
                const groupEditionSize = forceDiscontinuedCounts
                  ? group.owners.length
                  : group.editionSize;
                return (
                  <button
                    type="button"
                    key={group.key}
                    className={[
                      'limited-edition-owners-popup__tab-button',
                      isActive ? 'limited-edition-owners-popup__tab-button--active' : '',
                    ].filter(Boolean).join(' ')}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveSizeKey(group.key)}
                  >
                    <span className="limited-edition-owners-popup__tab-label">{group.label}</span>
                    <span className="limited-edition-owners-popup__tab-count">
                      {formatSoldCopy(groupSoldCount, groupEditionSize)}
                    </span>
                  </button>
                );
              })}
            </div>

            {activeGroup ? (
              <div
                className="limited-edition-owners-popup__list"
                onScroll={handleListScroll}
              >
                {hasOwners ? (
                  ownersToDisplay.map((owner, index) => {
                    const displayName = owner.isPrivate ? 'Private collector' : owner.name ?? owner.username ?? '-';
                    const canNavigate = owner.isPrivate ? false : Boolean(owner.username);
                    const editionSizeValue = forceDiscontinuedCounts
                      ? activeOwners.length
                      : (normalizeEditionSize(owner.editionSize)
                        ?? normalizeEditionSize(activeGroupEditionSize)
                        ?? activeGroupEditionSize
                        ?? owner.editionSize
                        ?? null);
                    const hasEditionNumber =
                      !forceDiscontinuedCounts && (owner.editionNumber || owner.editionNumber === 0);
                    const fallbackEditionNumber = forceSequentialEditionNumbers ? index + 1 : null;
                    const editionNumberLabel = hasEditionNumber
                      ? owner.editionNumber
                      : (fallbackEditionNumber ?? '--');
                    const editionFraction = editionSizeValue
                      ? `${editionNumberLabel}/${editionSizeValue}`
                      : (hasEditionNumber || forceSequentialEditionNumbers)
                        ? `${editionNumberLabel}`
                        : null;
                    const ownerContent = canNavigate ? (
                      <button
                        type="button"
                        className="limited-edition-owners-popup__owner-link"
                        onClick={() => handleProfileNavigate(owner.username)}
                      >
                        {displayName}
                      </button>
                    ) : (
                      <span className="limited-edition-owners-popup__owner-label">
                        {displayName}
                      </span>
                    );

                    return (
                      <div key={owner.id} className="limited-edition-owners-popup__row">
                        <div className="limited-edition-owners-popup__owner">
                          <UserProfileImage
                            image={owner.imageId}
                            typeOfProfile={owner.type ?? 'collector'}
                            className="limited-edition-owners-popup__avatar"
                            onClick={canNavigate ? () => handleProfileNavigate(owner.username) : undefined}
                          />
                          <div className="limited-edition-owners-popup__owner-texts">
                            {ownerContent}
                          </div>
                        </div>
                        <div className="limited-edition-owners-popup__row-meta">
                          <div className="limited-edition-owners-popup__edition-meta">
                            {editionFraction && (
                              <span className="limited-edition-owners-popup__edition-count">
                                {editionFraction}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="limited-edition-owners-popup__empty limited-edition-owners-popup__empty--inline">
                    No public collectors for this size yet.
                  </p>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </GenericPopup>
  );
}
